import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { sortedPosts } from "@/data/blog";

/**
 * Homepage "From the Blog" — editorial, content-first, scalable.
 *
 * Layout per post: horizontal card with content LEFT (category, title,
 * excerpt, meta, CTA) and a compact controlled-size preview RIGHT
 * (~180-220px wide, fixed aspect, framed). Stacks to vertical on
 * mobile.
 *
 * Renders latest 2-3 posts max. Scales cleanly to 5, 10+ posts because
 * each row is self-contained — never an oversized hero.
 */
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const HomeBlogPreview = () => {
  const posts = sortedPosts().slice(0, 3);
  if (posts.length === 0) return null;

  return (
    <section
      className="py-20 md:py-24 px-6 md:px-12 bg-[#0A0A0A] border-t border-neutral-900"
      aria-labelledby="home-blog-heading"
      data-testid="home-blog-preview"
    >
      <div className="max-w-5xl mx-auto">
        {/* Section header — compact, restrained */}
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-12 flex-wrap">
          <div className="max-w-xl">
            <motion.p
              className="text-purple-400 uppercase text-xs tracking-widest mb-3 font-semibold"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              From the Blog
            </motion.p>
            <motion.h2
              id="home-blog-heading"
              className="text-2xl md:text-3xl lg:text-[34px] font-unbounded font-bold leading-tight"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Podcast SEO, distribution, and AI search — explained.
            </motion.h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white border border-neutral-800 hover:border-purple-500/50 rounded-full px-4 py-2.5 transition-colors"
            data-testid="home-blog-view-all"
          >
            All articles
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Editorial card list — one row per article, content-first */}
        <ul
          className="divide-y divide-neutral-900 border-y border-neutral-900"
          data-testid="home-blog-list"
        >
          {posts.map((p, i) => (
            <motion.li
              key={p.slug}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link
                to={`/blog/${p.slug}`}
                className="group block py-6 md:py-7"
                data-testid={`home-blog-card-${p.slug}`}
              >
                <article className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-6 md:gap-8 items-center">
                  {/* Content — left */}
                  <div className="order-2 md:order-1">
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] uppercase tracking-wider text-neutral-500 mb-3 font-semibold">
                      <span className="text-purple-300">{p.category}</span>
                      <span className="text-neutral-700">·</span>
                      <span>{formatDate(p.publishedAt)}</span>
                      <span className="text-neutral-700">·</span>
                      <span className="inline-flex items-center gap-1 normal-case tracking-normal text-neutral-500">
                        <Clock className="w-3 h-3" />
                        {p.readingTime}
                      </span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-unbounded font-semibold text-white leading-snug mb-2 group-hover:text-purple-100 transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm md:text-[15px] text-neutral-400 leading-relaxed mb-4 line-clamp-2 md:line-clamp-2">
                      {p.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-300 group-hover:text-white transition-colors">
                      Read article
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>

                  {/* Visual — right, compact + framed */}
                  <div className="order-1 md:order-2 w-full md:w-[180px]">
                    <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-[#0A0913] border border-neutral-800 group-hover:border-purple-500/40 transition-colors">
                      <img
                        src={p.cover.src}
                        alt={p.cover.alt}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
                      />
                      {/* Subtle gradient overlay for premium framing */}
                      <div
                        className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </article>
              </Link>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default HomeBlogPreview;
